import {
  Linking,
  Platform,
  ScrollView,
  View,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  Text,
} from 'react-native'
import { classes, mesaColorNames, typography } from '@/src/common/theme'
import { useRouter } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import * as Application from 'expo-application'
import { openBrowserAsync } from 'expo-web-browser'
import type { PropsWithChildren, ComponentProps } from 'react'
import { useSignout } from '@/src/common/hooks/useSignout'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { environment } from '@/src/common/environment'
import { useMediaQuery } from 'react-responsive'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { triggerSegmentAction, type SegmentAction } from '@/src/services/segment'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
// import { useReferralParameterStore } from '@/src/services/Statsig/useReferralParameterStore'
import { Section } from '@/src/common/components/Section'
import { maxWidth, useMinWidth1024 } from '@/src/common/wrappers/MainWrapper'
import { version } from '@/package.json'
import * as Updates from 'expo-updates'
// import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'

export function useMenuIsFullWidth() {
  return useMediaQuery({ maxWidth: 640 })
}

const minVerticalMargin = 48

/**
 * parent is recommended to be a full-screen _columnar_ flexbox
 *
 * @todo consider including the recommended parent structure in this component
 */
export function ModalMenuScrollView(
  props: ComponentProps<typeof ScrollView> & {
    footer?: PropsWithChildren['children']
  },
) {
  const menuIsFullWidth = useMenuIsFullWidth()
  const safeAreaInsets = useSafeAreaInsets()
  const minWidth1024 = useMinWidth1024()
  const margin = menuIsFullWidth ? 16 : minWidth1024 ? 40 : 24
  const { width: windowWidth } = useWindowDimensions()

  return (
    <>
      <View style={{ width: 0, flexBasis: `17%`, flexShrink: 1, minHeight: minVerticalMargin }} />
      <View
        style={[
          {
            position: menuIsFullWidth ? 'absolute' : 'relative',
            backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
            marginTop: menuIsFullWidth ? margin : 0,
            marginVertical: margin,
            marginHorizontal:
              windowWidth > maxWidth ? (windowWidth - maxWidth) / 2 + margin : margin,
            borderRadius: 16,
          },
          menuIsFullWidth
            ? safeAreaInsets
            : {
                width: '100%',
                maxWidth: 484,
                marginTop: 0,
                marginHorizontal: 'auto',
                marginVertical: 0,
                flexGrow: 0,
                flexShrink: 1,
                flexBasis: 'auto',
              },
          classes.cardShadow,
        ]}
      >
        <ScrollView
          stickyHeaderIndices={[0]}
          {...props}
          style={[
            {
              padding: 24,
              paddingTop: 0,
            },
            props.style,
          ]}
          contentContainerStyle={[
            { display: 'flex', flexDirection: 'column', alignItems: 'stretch' },
            props.contentContainerStyle,
          ]}
        >
          {props.children}
        </ScrollView>
        {props.footer}
      </View>
      <View style={{ width: 0, flexBasis: `17%`, flexGrow: 1, minHeight: minVerticalMargin }} />
    </>
  )
}

export default function Menu() {
  const router = useRouter()
  const isMesaEmployee = useFeatureGate(FeatureGates.MesaEmployees).featureGate?.value
  const isEndToEndUser = useFeatureGate(FeatureGates.EndToEndUsers).featureGate?.value
  // Refer a Friend temporarily hidden - see https://mesamember.slack.com/archives/C07JK5U3H8S/p1764867423105289
  // const { sourcePartnerName } = useSourcePartnerName()
  // const referralStore = useReferralParameterStore()
  // const disabledPartners = referralStore.get('disabled_partners')
  // const entryText = referralStore.get('entry_text', 'Refer a Friend')
  // const isDisabledPartner = disabledPartners?.includes(sourcePartnerName)
  const handleLogoutPress = useSignout()

  interface MenuItem extends Pick<ComponentProps<typeof TouchableOpacity>, 'onPress'> {
    icon?: PropsWithChildren['children']
    title: string
    segmentAction?: SegmentAction
    testId?: string
  }

  const accountSettings = [
    {
      title: 'Account',
      onPress: () => {
        router.dismissTo(Routes.PiiMenu.route)
      },
      segmentAction: 'User Pressed the Settings Item Account',
    },
    {
      title: 'Feedback',
      onPress: () =>
        void (async () => {
          try {
            await Linking.openURL('mailto:support@mesamember.com')
          } catch {
            await openBrowserAsync('https://mesamember.com')
          }
        })(),
      segmentAction: 'User Pressed the Settings Item Feedback',
    },
    {
      title: 'Support',
      onPress: () => void openInAppBrowser('https://support.mesamember.com', 'Support'),
      segmentAction: 'User Pressed the Settings Item Support',
    },
    {
      title: 'Website',
      onPress: () => void openInAppBrowser('https://mesamember.com', 'Mesa'),
      segmentAction: 'User Pressed the Settings Item Website',
    },
    {
      title: 'Legal',
      onPress: () => void openInAppBrowser('https://www.mesamember.com/legal', 'Legal'),
      segmentAction: 'User Pressed the Settings Item Legal',
    },
    ...(isMesaEmployee
      ? [
          {
            title: 'Storybook',
            onPress: () => router.dismissTo('/main/storybook'),
            segmentAction: 'User Pressed the Settings Item Storybook',
          } as const,
        ]
      : []),
    ...(isEndToEndUser
      ? [
          {
            title: 'Internal Testing',
            onPress: () => null,
            testId: 'Authentication.Text.FeatureGateForEndToEndUsers',
          } as const,
        ]
      : []),
    // ...(!isDisabledPartner
    //   ? [
    //       {
    //         title: entryText,
    //         onPress: () => router.dismissTo('/main/refer-a-friend'),
    //         segmentAction: 'User Pressed the Settings Item Refer a Friend',
    //       } as const,
    //     ]
    //   : []),
    {
      title: 'Signout',
      onPress: handleLogoutPress,
      segmentAction: 'User Logged Out',
      testId: 'Authentication.Press.SignOut',
    },
  ] satisfies MenuItem[]

  const versionString = `v${Updates.updateId ? `${version}@${Updates.updateId}` : version}${
    Platform.OS === 'web' ? '' : ` (${Application.nativeBuildVersion})`
  }` as const

  const { height: windowHeight } = useWindowDimensions()

  return (
    <Animated.View
      entering={FadeIn}
      exiting={FadeOut}
      style={[StyleSheet.absoluteFill, { width: '100%', height: '100%' }]}
    >
      <TouchableOpacity
        activeOpacity={1}
        onPress={() => router.dismiss()}
        style={[StyleSheet.absoluteFill, { backgroundColor: '#00000050' }]}
      >
        <View style={{ height: windowHeight * 2 }} />
      </TouchableOpacity>
      <ModalMenuScrollView>
        <Section
          styleOverride={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            margin: 0,
            height: 'auto',
            paddingBottom: 6,
            paddingTop: 24,
            backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
          }}
          title="Menu"
          titleStyles={[
            {
              color: mesaColorNames['Mesa - Brand/Secondary #223843'],
              marginLeft: 0,
            },
            typography['label/lg-bold'],
          ]}
          onClosePress={() => router.dismiss()}
          closeStyle={{ position: 'relative', marginLeft: 'auto', right: 0 }}
        />

        <View style={{ display: 'flex', flexDirection: 'column' }}>
          {accountSettings.map((accountSetting) => {
            const isLast = accountSettings.at(-1) === accountSetting
            return (
              <TouchableOpacity
                style={{
                  paddingVertical: 6,
                  marginBottom: isLast ? 32 : 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                testID={`${accountSetting.testId}`}
                key={accountSetting.title}
                onPress={() => {
                  if (accountSetting.segmentAction) {
                    void triggerSegmentAction({
                      component: 'Main Menu',
                      action: accountSetting.segmentAction,
                    })
                  }

                  accountSetting.onPress()
                }}
              >
                <Text
                  style={{
                    color: isLast
                      ? mesaColorNames['semantic/fg/error #DB340B']
                      : mesaColorNames['Mesa - Brand/Blackish #222222'],
                  }}
                >
                  {accountSetting.title}
                </Text>
                {!isLast && (
                  <ChevronRight
                    style={{ flexShrink: 0 }}
                    color={mesaColorNames['semantic/fg/base #131214']}
                  />
                )}
              </TouchableOpacity>
            )
          })}
        </View>

        {!(Platform.OS === 'web' && environment === 'production') && (
          <Text style={styles.appVersionText}>
            {versionString} - {environment}
          </Text>
        )}
      </ModalMenuScrollView>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  appVersionText: { color: mesaColorNames['Greyscale/500 #6E7375'], fontSize: 12 },
})
